import { Component, forwardRef, Input, inject, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import {
	CONTINENTS,
	COUNTRIES_BY_CONTINENT,
	type Continent,
	continentOf,
	countryName,
} from '@libs/dtos/merit/countries.catalog';
import { SelectComponent } from '@shared/components/select/select.component';
import { SharedModule } from '@shared/modules/shared.module';

/**
 * Nacionalidad en cascada: continente → país. El valor del control es el
 * **código ISO de dos letras**, nunca un nombre.
 *
 * Por qué dos desplegables y no un autocompletado de 200 entradas: el campo era
 * texto libre y acabó con tres formatos a la vez en el mismo entorno —países en
 * español, gentilicios en inglés y minúsculas sueltas—. Si se puede teclear, se
 * teclea mal; aquí lo único que se teclea es el filtro de una lista cerrada.
 *
 * El continente NO se guarda: se deriva del país con `continentOf()`. Guardarlo
 * daría dos verdades para el mismo dato y la primera vez que discreparan nadie
 * sabría cuál manda.
 *
 * Los nombres de país los da `Intl` en el idioma activo, así que la lista se
 * traduce sola: en i18n solo viven los cinco continentes.
 *
 * `display: contents` en el host (ver el SCSS): el componente pinta DOS campos y
 * los formularios que lo usan son rejillas de 12 columnas. Sin eso, los dos
 * desplegables caerían dentro de una sola celda y no habría forma de darles
 * anchos distintos desde fuera.
 */
@Component({
	selector: 'app-country-select',
	standalone: true,
	imports: [SharedModule, SelectComponent],
	templateUrl: './country-select.component.html',
	styleUrl: './country-select.component.scss',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => CountrySelectComponent),
			multi: true,
		},
	],
})
export class CountrySelectComponent implements ControlValueAccessor {
	private readonly transloco = inject(TranslocoService);

	/** Clase de la celda de cada desplegable, para encajar en la rejilla del formulario. */
	@Input() continentFieldClass = 'qs-field qs-col-6';
	@Input() countryFieldClass = 'qs-field qs-col-6';
	/** Pinta el asterisco de obligatorio en las dos etiquetas. */
	@Input() required = true;
	/** Label of the country cell. The candidate's own country reads «País de nacimiento» (07/10). */
	@Input() countryLabelKey = 'common.countrySelect.country';
	/**
	 * Distintivo junto a la etiqueta del país (el asistente lo usa para «sugerido
	 * del CV»). Llega como texto y no por proyección de contenido a propósito: los
	 * estilos del padre no cruzan la encapsulación, así que un `<span>` proyectado
	 * llegaría aquí sin ninguno de ellos.
	 */
	@Input() badge: string | null = null;
	/** Marca los dos desplegables en rojo (el error es del par, no de uno). */
	@Input() error = false;
	@Input() size: 'small' | 'medium' | 'large' = 'medium';
	/**
	 * `null` mantiene el desplegable dentro del formulario. Hace falta cuando el
	 * campo vive en un contenedor con scroll propio: con `appendTo="body"` el
	 * panel se queda flotando donde se abrió al desplazar.
	 */
	@Input() appendTo: 'body' | null = 'body';

	readonly countryCode = signal<string | null>(null);
	readonly continent = signal<Continent | null>(null);

	disabled = false;

	private onChange: (value: string | null) => void = () => undefined;
	private onTouched: () => void = () => undefined;

	// ---- ControlValueAccessor ----------------------------------------------

	writeValue(value: string | null): void {
		const code = value ? value.toUpperCase() : null;
		this.countryCode.set(code);
		// El continente se deriva SIEMPRE del país: es lo que precarga los dos
		// desplegables al editar una ficha que ya tiene país.
		this.continent.set(continentOf(code));
	}

	registerOnChange(fn: (value: string | null) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
	}

	// ---- Options ------------------------------------------------------------

	/** Los cinco continentes, con su etiqueta traducida. */
	get continentOptions(): { label: string; value: Continent }[] {
		return CONTINENTS.map((value) => ({
			label: this.transloco.translate(`common.countrySelect.continents.${value}`),
			value,
		})).sort((a, b) => a.label.localeCompare(b.label, this.locale));
	}

	/**
	 * Los países del continente elegido, ordenados por el nombre que se ve. Vacío
	 * mientras no haya continente: es lo que hace que el segundo desplegable no
	 * ofrezca doscientas entradas antes de acotar.
	 */
	get countryOptions(): { label: string; value: string }[] {
		const continent = this.continent();
		if (!continent) return [];
		const locale = this.locale;
		return COUNTRIES_BY_CONTINENT[continent]
			.map((code) => ({ label: countryName(code, locale), value: code }))
			.sort((a, b) => a.label.localeCompare(b.label, locale));
	}

	private get locale(): string {
		return this.transloco.getActiveLang();
	}

	// ---- Events -------------------------------------------------------------

	onContinentChange(continent: Continent | null): void {
		this.continent.set(continent);
		const code = this.countryCode();
		// Cambiar de continente invalida el país elegido, salvo que siga siendo de
		// ese continente (pasa al reabrir el desplegable y elegir lo mismo). Dejarlo
		// puesto guardaría un país que el desplegable de al lado ni siquiera lista.
		if (code && continentOf(code) !== continent) this.setCode(null);
	}

	onCountryChange(code: string | null): void {
		this.setCode(code);
	}

	private setCode(code: string | null): void {
		this.countryCode.set(code);
		this.onChange(code);
	}

	touch(): void {
		this.onTouched();
	}
}
